import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { ConfigBaseFilter, ConfigService, ListLayout, ListSortOption } from 'gung-list';
import { catchError, filter, map, Observable, of, switchMap, tap } from 'rxjs';
import { Product } from '../../models';
import { AssortmentService } from '../assortment.service';
import { AuthService } from '../auth/auth.service';
import {
  BaseViewConfigService,
  CurrentAssortmentService,
  ProductViewType
} from '../base-view-config/base-view-config.service';
import { FavouriteProductsService } from '../favourite-products/favourite-products.service';
import { ProductListConfigService } from '../product-list-config/product-list-config.service';
import { ProductListV2ConfigService } from '../product-list-v2-config/product-list-v2-config.service';
import { ProductService } from '../products/product.service';

@Injectable({
  providedIn: 'root'
})
export class FavouriteProductListConfigService implements ConfigService<Product> {
  protected productService = inject(ProductService);
  protected baseViewConfigService = inject(BaseViewConfigService);
  protected productListConfigService = inject(ProductListConfigService);
  protected productListV2ConfigService = inject(ProductListV2ConfigService);
  protected authService = inject(AuthService);
  protected favouritesService = inject(FavouriteProductsService);
  currentAssortmentService = inject(CurrentAssortmentService);
  assortmentService = inject(AssortmentService);

  topFilter?: boolean;

  getFilters(route?: ActivatedRouteSnapshot): ConfigBaseFilter<Product>[] {
    if (this.baseViewConfigService.productViewType === ProductViewType.productV2) {
      return this.productListV2ConfigService.getFilters();
    } else {
      return this.productListConfigService.getFilters();
    }
  }
  getSortOptions(route?: ActivatedRouteSnapshot): ListSortOption<Product>[] {
    return this.productListConfigService.getSortOptions();
  }
  getBatchSizes(route?: ActivatedRouteSnapshot): number[] {
    return this.productListConfigService.getBatchSizes();
  }
  getLayouts(route?: ActivatedRouteSnapshot): ListLayout<Product>[] {
    if (this.baseViewConfigService.productViewType === ProductViewType.productV2) {
      return this.productListV2ConfigService.getLayouts();
    } else {
      return this.productListConfigService.getLayouts();
    }
  }
  getSearchTerms(item: Product): string[] {
    return this.productListConfigService.getSearchTerms(item);
  }
  getItemId(item: Product): string {
    return this.productListConfigService.getItemId(item);
  }

  getItems(): Observable<Product[]> {
    return this.favouritesService.getFavourites().pipe(
      map(favourites => Object.keys(favourites).filter(id => favourites[id])),
      switchMap(filteredIds =>
        this.authService.getCurrentUser().pipe(
          filter(user => !!user),
          switchMap(user =>
            this.assortmentService.getPimAssortmentData(user.assortment).pipe(
              tap(assortment => {
                if (assortment) {
                  this.currentAssortmentService.currentAssortment = assortment;
                }
              }),
              catchError(err => {
                //ignore assorment endpoint error to avoid break product list
                return of(null);
              }),
              switchMap(() => this.productService.getProductsByIds(filteredIds))
            )
          )
        )
      )
    );
  }
}
